/**
 * Runtime feature-flag plumbing.
 *
 * Fetches `GET /api/config` once on mount, caches in React context, and
 * fails CLOSED on every error mode (network error, non-2xx, malformed
 * body, timeout at 3 s). The gate component downstream renders the
 * standard `<NotFound />` for the two Verified Documents routes when the
 * flag is false. See PRD release-note #7.
 *
 * Public exports:
 *   - fetchFeatureFlags({apiBase, timeoutMs, fetchImpl}) : async pure
 *       function, directly unit-testable without React.
 *   - FeatureFlagsContext : React context, defaults to closed.
 *   - FeatureFlagsProvider : wraps the app, performs the one-shot fetch.
 *   - useFeatureFlags() : hook consumers call to read the current state.
 */
import { createContext, useContext, useEffect, useState } from "react";
import React from "react";

const DEFAULT_API = process.env.REACT_APP_BACKEND_URL || "";
const DEFAULT_TIMEOUT_MS = 3000;

export const CLOSED_FLAGS = Object.freeze({ verified_docs_enabled: false });

/**
 * Pure async: do the fetch, parse, return a flag object. Any failure
 * mode → closed-default. Separated out so the Jest suite can exercise
 * every branch without a React render.
 */
export async function fetchFeatureFlags({
  apiBase = DEFAULT_API,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  fetchImpl = typeof fetch !== "undefined" ? fetch : null,
} = {}) {
  if (!fetchImpl) {
    return { ...CLOSED_FLAGS };
  }
  const controller =
    typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer =
    controller && timeoutMs > 0
      ? setTimeout(() => controller.abort(), timeoutMs)
      : null;
  try {
    const resp = await fetchImpl(`${apiBase}/api/config`, {
      signal: controller ? controller.signal : undefined,
    });
    if (!resp || !resp.ok) {
      return { ...CLOSED_FLAGS };
    }
    const body = await resp.json();
    if (
      !body ||
      typeof body !== "object" ||
      typeof body.verified_docs_enabled !== "boolean"
    ) {
      return { ...CLOSED_FLAGS };
    }
    return { verified_docs_enabled: body.verified_docs_enabled };
  } catch (_err) {
    return { ...CLOSED_FLAGS };
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export const FeatureFlagsContext = createContext({
  ...CLOSED_FLAGS,
  loading: true,
});

export function FeatureFlagsProvider({ children }) {
  const [state, setState] = useState({ ...CLOSED_FLAGS, loading: true });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await fetchFeatureFlags();
      if (!cancelled) {
        setState({ ...result, loading: false });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <FeatureFlagsContext.Provider value={state}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlags() {
  return useContext(FeatureFlagsContext);
}
